import { useMemo, useState } from 'react'

import { useAllTransactions, useLoadingTransactions } from '@/modules/projects'
import {
  accountStatusInSegment,
  worseStatus,
} from '@/modules/projects/components/StatementCoverage/deriveCoverage'
import { useStatementCoverage } from '@/modules/projects/components/StatementCoverage/useStatementCoverage'
import type { DirectoryAccount } from '@/modules/projects/stores/types'
import { firstNumberOf } from './accounts'
import {
  coverageIndex,
  lanesHolding,
  type StatusFor,
  tallyCoverage,
  withEmptyMonths,
} from './coverage'
import { type ProjectSplit, segmentIdOf } from './fromProject'
import type { MapToolbarProps } from './MapToolbar'
import { buildMap, hiddenLanes, yearsOf } from './map'
import {
  filterMap,
  type Highlight,
  type MapFilters,
  NO_FILTERS,
} from './mapView'
import { memberCardsOf } from './memberCardsOf'
import type { Account, DatePeriod, StatementView } from './types'

interface MapCoverageInput {
  split: ProjectSplit
  views: StatementView[]
  directory: DirectoryAccount[]
  accountOfStatement: (statementId: string) => Account | null
}

// The map in the coverage grid's own colours, cut down to the years and the colour picked out.
export const useMapCoverage = ({
  split,
  views,
  directory,
  accountOfStatement,
}: MapCoverageInput) => {
  const { accounts } = split.arrangement
  const coverage = useStatementCoverage()
  const transactions = useAllTransactions()
  const coverageArriving = useLoadingTransactions() && transactions.length === 0
  // No rows read yet means no verdict either way, so the map paints nothing rather than a red wash
  // that flips to green a second later.
  const liveCoverage = coverageArriving ? null : coverage
  const [filters, setFilters] = useState<MapFilters>(NO_FILTERS)
  const [highlight, setHighlight] = useState<Highlight>(null)

  const coverageByNumber = useMemo(
    () => coverageIndex(liveCoverage?.accounts ?? []),
    [liveCoverage],
  )

  // A bar on the map runs across exactly the months the coverage page colours for it.
  const map = useMemo(() => {
    const monthsOwnedBy = (held: StatementView) => {
      const segmentId = segmentIdOf(held.statement.id)
      const last4 = firstNumberOf(accounts, held.statement.accountId)
      if (segmentId === null || !last4) return []
      return coverageByNumber.ownedMonths.get(last4)?.get(segmentId) ?? []
    }
    return buildMap(
      accounts,
      views,
      monthsOwnedBy,
      memberCardsOf(liveCoverage?.accounts ?? [], directory),
    )
  }, [accounts, views, coverageByNumber, liveCoverage, directory])

  // The verdict a month is painted in is the coverage grid's own, read for one account, so a
  // statement is the same colour here and on the coverage page.
  const statusFor: StatusFor = (statementId, last4) => {
    const segment = split.segments.get(statementId)
    const numbers = last4
      ? [last4]
      : (accountOfStatement(statementId)?.numbers ?? [])
    if (!segment || numbers.length === 0) return 'not_checked'
    return numbers
      .map((number) => accountStatusInSegment(segment, number))
      .reduce(worseStatus)
  }

  // Outside the account's own span there is nothing to say: the project simply starts and ends
  // where it does.
  const coverageOfEmptyMonth = (last4: string | null, period: DatePeriod) => {
    const months = last4
      ? coverageByNumber.monthsByNumber.get(last4)
      : undefined
    return months?.get(period.start.slice(0, 7)) ?? null
  }

  const shownMap = filterMap(map, filters)
  const shownLanes = withEmptyMonths(shownMap.lanes, coverageOfEmptyMonth)
  const visibleLanes = lanesHolding(shownLanes, highlight, statusFor)

  const toolbar: MapToolbarProps = {
    years: yearsOf(map.lanes),
    filters,
    counts: tallyCoverage(shownLanes, statusFor),
    coverageArriving,
    highlight,
    hidden: hiddenLanes(map.lanes, visibleLanes),
    onFilters: setFilters,
    onHighlight: setHighlight,
    onShowAllAccounts: () => {
      setFilters(NO_FILTERS)
      setHighlight(null)
    },
  }

  return {
    shownMap: { ...shownMap, lanes: visibleLanes },
    filters,
    highlight,
    statusFor,
    toolbar,
  }
}
